import { useEffect } from 'react';
import { siteConfig } from '@/mocks/site';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalPath?: string;
  ogType?: string;
  ogImage?: string;
  schema?: Record<string, unknown>[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalPath = '/',
  ogType = 'website',
  ogImage,
  schema = [],
}: SEOHeadProps) {
  const pageTitle = title ? `${title} - ${siteConfig.name}` : `${siteConfig.name} - ${siteConfig.tagline}`;
  const pageDescription = description || siteConfig.description;
  const pageKeywords = keywords || siteConfig.keywords;
  const canonicalUrl = `${siteConfig.url}${canonicalPath}`;

  useEffect(() => {
    document.title = pageTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', pageDescription);
    setMeta('keywords', pageKeywords);

    const link = document.querySelector('link[rel="canonical"]');
    if (link) {
      link.setAttribute('href', canonicalUrl);
    } else {
      const newLink = document.createElement('link');
      newLink.setAttribute('rel', 'canonical');
      newLink.setAttribute('href', canonicalUrl);
      document.head.appendChild(newLink);
    }

    setMeta('og:title', pageTitle, true);
    setMeta('og:description', pageDescription, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:site_name', siteConfig.name, true);
    if (ogImage) setMeta('og:image', ogImage, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', pageTitle);
    setMeta('twitter:description', pageDescription);

    const existingScripts = document.querySelectorAll('script[data-seo-schema]');
    existingScripts.forEach((s) => s.remove());

    schema.forEach((s, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-schema', `schema-${i}`);
      script.textContent = JSON.stringify(s);
      document.head.appendChild(script);
    });

    return () => {
      const cleanScripts = document.querySelectorAll('script[data-seo-schema]');
      cleanScripts.forEach((s) => s.remove());
    };
  }, [pageTitle, pageDescription, pageKeywords, canonicalUrl, ogType, ogImage, schema]);

  return null;
}